import { ELEMENT_ID_RE, elementIdOf, elementIdSegment } from './kql';
import { sameKqlFamily } from './analytics';
import { resolveCardNode } from './cards';
import { normalizeId } from './ids';

/**
 * A CHART'S SPEC FIELDS BY ID (KPR-238 P6, ruled D2; keepr-web
 * docs/plans/element-ids-writers-and-closing.md §7). A measure's `element` and
 * the `on` of `groupBy`, `splitBy` and `time` name an element by its name or
 * by its id, spelled as KQL spells one — `#k7f3q2xa` (an element name starts
 * with a letter or a digit, so the two never meet). The server resolves an id
 * on the chart's card (keepr-api `utils/analytics/spec.js` elementOfField) and
 * stores each field in one spelling: names in deploy 1, ids in deploy 2.
 *
 *   SpecIdWrites.ON             whether the builder WRITES ids (deploy 2, the
 *                               twin of keepr-api utils/analytics/specIdWrites.js);
 *                               off, it writes names, an id it read included
 *   fieldIdOf(ref)              the id a field names, or null for a name
 *   cardElementChain(cardId, cardsById, elementSetsById)
 *                               every element the card's chain carries, not
 *                               merged — what an id a restatement replaced
 *                               names (`shadowed` below)
 *   elementForField(list, ref, { shadowed })
 *                               the element of a card a field names, or null
 *   fieldRefFor(element)        how the builder writes an element into a field
 *   chartSpecForDisplay(spec, list, shadowed)
 *                               the spec with each id field as its element's
 *                               name, for the words a chart says about itself
 *   columnRefOf · columnForField · columnRefFor · columnsOfGroup
 *                               a repeating group's column as a field,
 *                               `<group>.<column>` (KPR-684)
 *
 * No bare id is ever shown: a field whose element the card no longer holds
 * reads as nothing (`''`) where a chart says its fields, and the builder says
 * "an element that is no longer here".
 */

// Mutable so a test can flip it, as keepr-api's tests flip theirs. ON since
// deploy 2 (the writers); its rollback is deploy 1's `false`, which still reads ids.
export const SpecIdWrites = { ON: true };

/** The words for a field whose element the card no longer holds (kqlBuilder's GONE_ELEMENT_LABEL). */
export const GONE_FIELD_WORDS = 'an element that is no longer here';

/**
 * Every element the card's chain CARRIES — the card's own and its element
 * sets', then its parent's, up to the root — not merged by name, so an
 * inherited element a restatement replaced is still there to be found by its
 * id (the server's `carriedElements`, keepr-api utils/kql/compile.js: a
 * restatement anywhere up the chain, KPR-238 P6's W2). Bounded and cycle-safe.
 */
export function cardElementChain(cardId, cardsById, elementSetsById = null) {
	const out = [];
	const seen = new Set();
	let id = normalizeId(cardId);
	for (let hops = 0; id && hops < 32 && !seen.has(id); hops += 1) {
		seen.add(id);
		const card = cardsById && Object.prototype.hasOwnProperty.call(cardsById, id) ? cardsById[id] : null;
		if (!card) break;
		const node = resolveCardNode(card, elementSetsById);
		(Array.isArray(node.elements) ? node.elements : []).forEach((el) => { if (el) out.push(el); });
		id = normalizeId(card.parentCardId);
	}
	return out;
}

/** The id a spec field names (`'#k7f3q2xa'` → `'k7f3q2xa'`), or null when it is a name (or not text). */
export function fieldIdOf(ref) {
	return typeof ref === 'string' ? elementIdOf(ref) : null;
}

const validId = (value) => typeof value === 'string' && ELEMENT_ID_RE.test(value);

/**
 * The element of a card that spec field `ref` names, among `list` — the card's
 * elements as they apply (CardDefinitionsContext's getElementsForCard: its own,
 * its ancestors', its element sets', a child's restatement winning) — or null.
 * A name is that element. An id is the element that carries it; else, where
 * the card replaces an inherited element under the same name — anywhere up
 * its chain (`shadowed`: cardElementChain) — the one it is replaced by, when
 * the two read alike (the server's same-family rule); else the one copy that
 * records it as its origin (`derivedFrom`, `derivedVia`). Never one of
 * several: that is null.
 */
export function elementForField(list, ref, { shadowed = [] } = {}) {
	const elements = Array.isArray(list) ? list.filter(Boolean) : [];
	if (typeof ref !== 'string' || !ref) return null;
	const id = fieldIdOf(ref);
	if (id === null) return elements.find((el) => el.name === ref) || null;
	const own = elements.find((el) => el.id === id);
	if (own) return own;
	const replaced = (Array.isArray(shadowed) ? shadowed : []).find((el) => el && el.id === id);
	if (replaced) {
		const applying = elements.find((el) => el.name === replaced.name);
		return applying && sameKqlFamily(applying, replaced) ? applying : null;
	}
	const copies = elements.filter((el) => el.derivedFrom === id || el.derivedVia === id);
	return copies.length === 1 ? copies[0] : null;
}

// ------------------------------------------------- a repeating group's columns
//
// KPR-684: a COLUMN of a repeating group is a spec field written
// `<group>.<column>` (keepr-api spec.js columnRefOf), each part a name or an
// `#id` — an element name holds no dot, so the first dot splits it. The
// system field `created.by` is never a column.

const SYSTEM_DOTTED = ['created.by'];
const GROUP_TYPE = 'repeating-group';

/** `{ group, column }` — the two parts a column field names — or null for any other field. */
export function columnRefOf(ref) {
	if (typeof ref !== 'string' || SYSTEM_DOTTED.indexOf(ref) !== -1) return null;
	const at = ref.indexOf('.');
	if (at <= 0 || at === ref.length - 1) return null;
	return { group: ref.slice(0, at), column: ref.slice(at + 1) };
}

/** A group's columns, as its definition lists them. */
export function columnsOfGroup(group) {
	const list = group && group.options && Array.isArray(group.options.columns) ? group.options.columns : [];
	return list.filter((c) => c && typeof c.name === 'string' && c.name);
}

/**
 * The group and column of a card that column field `ref` names, among `list`
 * (the card's elements, as elementForField reads them) — `{ group, column }`,
 * or null where the card holds no such group or the group no such column.
 */
export function columnForField(list, ref, { shadowed = [] } = {}) {
	const parts = columnRefOf(ref);
	if (!parts) return null;
	const group = elementForField(list, parts.group, { shadowed });
	if (!group || group.dataType !== GROUP_TYPE) return null;
	const id = fieldIdOf(parts.column);
	const column = columnsOfGroup(group).find((c) => (id !== null ? c.id === id : c.name === parts.column)) || null;
	return column ? { group, column } : null;
}

/** How the builder writes a column into a spec field: each part as fieldRefFor writes an element. */
export function columnRefFor(group, column) {
	const g = fieldRefFor(group);
	const c = fieldRefFor(column);
	return g && c ? `${g}.${c}` : null;
}

/** How the builder writes an element into a spec field: its `#id` while it writes ids and the element has one, else its name. */
export function fieldRefFor(element) {
	if (!element || typeof element.name !== 'string') return null;
	return SpecIdWrites.ON && validId(element.id) ? elementIdSegment(element.id) : element.name;
}

/**
 * `spec` with each element field that names its element by id written as that
 * element's current name — `''` where the card holds no such element (`list`,
 * its elements; `shadowed`, its chain's: cardElementChain) — for the words a
 * chart says about itself (its screen-reader sentence, the table
 * twin's first column, a note's "no Amount"). A copy; the same object when no
 * field is an id. Never sent anywhere: a write sends the spec as stored.
 */
export function chartSpecForDisplay(spec, list, shadowed = []) {
	if (!spec || typeof spec !== 'object') return spec;
	const named = (ref) => {
		// A column (KPR-684) is said by its own name: "4 Line entries have no amount".
		if (columnRefOf(ref)) {
			const col = columnForField(list, ref, { shadowed });
			return col ? col.column.name : '';
		}
		if (fieldIdOf(ref) === null) return ref;
		const el = elementForField(list, ref, { shadowed });
		return el ? el.name : '';
	};
	const fields = [
		...(Array.isArray(spec.measures) ? spec.measures.map((m) => m && m.element) : []),
		spec.groupBy && spec.groupBy.on,
		spec.splitBy && spec.splitBy.on,
		spec.time && spec.time.on,
		spec.entries,
	];
	if (!fields.some((ref) => fieldIdOf(ref) !== null || columnRefOf(ref))) return spec;
	const out = { ...spec };
	if (typeof spec.entries === 'string') out.entries = named(spec.entries);
	if (Array.isArray(spec.measures)) out.measures = spec.measures.map((m) => (m && typeof m.element === 'string' ? { ...m, element: named(m.element) } : m));
	['groupBy', 'splitBy', 'time'].forEach((key) => {
		if (spec[key] && typeof spec[key].on === 'string') out[key] = { ...spec[key], on: named(spec[key].on) };
	});
	return out;
}
